import { useEffect, useState } from 'react'

import { Heading } from '@hhgtech/hhg-components'
import { CommonGAssets } from '@hhgtech/hhg-components/gAssets'
import { Avatar, Button, Text } from '@hhgtech/hhg-components/mantine'
import { ArrowRight } from '@hhgtech/icons/other'
import { AspectRatio, Box, BoxProps, Flex, MantineTheme } from '@mantine/core'
import { useIntl } from 'react-intl'

import { Markdown } from 'components/atoms/markdown'
import { useIsMobile } from 'hooks/useIsMobile'
import { assetImagePath } from 'interfaces/constants'

import { Circle } from './circle'
import useStyles from './index.styles'

type RiskScreenerEmbedItemProps = {
  color: string
} & RiskScreenerItem &
  BoxProps

export type RiskScreenerItem = {
  bgColor: string
  thumbnail: string
  title: string
  reviewerName: string
  reviewerAvatar: string
  reviewerHospital: string
  reviewerSpecialty: string
  langdingPageUrlPrefix: string
  landingPageUrlSlug: string
}

const RiskScreenerEmbedItem = ({
  color,
  styles,
  className,
  thumbnail,
  title,
  reviewerAvatar,
  reviewerName,
  reviewerSpecialty,
  reviewerHospital,
  langdingPageUrlPrefix,
  landingPageUrlSlug,
  ...boxProps
}: RiskScreenerEmbedItemProps) => {
  const { formatMessage: f } = useIntl()
  const { classes, cx, theme } = useStyles(
    { color },
    {
      name: 'RiskScreenerEmbedItem',
      styles,
    },
  )
  const isMobile = useIsMobile()
  const listDes = [
    reviewerSpecialty && `<span>${reviewerSpecialty}</span>`,
    reviewerHospital && `<span>${reviewerHospital}</span>`,
  ].filter(Boolean)
  const [url, setUrl] = useState(
    `${langdingPageUrlPrefix}${landingPageUrlSlug}`,
  )
  const anchorProps = {
    href: url,
    target: '_blank',
    'data-event-category': 'Service Connection',
    'data-event-action': 'Risk Screening Banner Click',
    'data-event-label': url,
  }
  const content = (
    <Box className={cx(classes.root, className)} {...boxProps}>
      <Box className={classes.circle}>
        <Circle
          color={theme.fn.lighten(color, 0.23)}
          sx={(theme) => ({
            width: 120,
            height: 120,
            [theme.fn.largerThan('sm')]: {
              width: 240,
              height: 240,
            },
          })}
        />
      </Box>
      <Box className={classes.wrapper}>
        <Box className={classes.inner}>
          <Box className={classes.header}>
            <Box>
              <AspectRatio ratio={1} className={classes.imgWrapper}>
                <img
                  src={
                    thumbnail || CommonGAssets.getAssetPath('stethoscop.png')
                  }
                  alt={title}
                  loading="lazy"
                  style={{ objectFit: 'cover', width: '100%', height: '100%' }}
                />
              </AspectRatio>
            </Box>
            {title && (
              <Box
                sx={(theme: MantineTheme) => ({
                  [theme.fn.largerThan('sm')]: {
                    '.item-title': {
                      display: '-webkit-box',
                      '-webkit-line-clamp': '2',
                      '-webkit-box-orient': 'vertical',
                      overflow: 'hidden',
                    },
                  },
                })}
              >
                <a {...anchorProps}>
                  <Heading tag="h4" className="item-title" color="#fff">
                    {title}
                  </Heading>
                </a>
              </Box>
            )}
          </Box>
          {reviewerName && (
            <Flex gap={8} align={'center'} className={classes.reviewer}>
              <Avatar
                src={reviewerAvatar}
                size={32}
                withIndicator
                indicatorConfig={{
                  size: 18,
                  label: (
                    <img
                      src={`${assetImagePath}/reviewer-medical.svg`}
                      loading="lazy"
                      alt="badge"
                    />
                  ),
                  offset: 6,
                }}
                styles={{
                  root: {
                    backgroundColor: 'white',
                    borderRadius: '50%',
                  },
                  placeholder: {
                    border: 0,
                  },
                }}
              />
              <Box>
                <Text
                  size="p4"
                  color="#fff"
                  as="div"
                  sx={{
                    '.si-review-by-name': {
                      'strong,b': {
                        fontWeight: 600,
                      },
                      a: {
                        color: 'inherit',
                        '&:hover': {
                          color: '#2d87f3',
                        },
                      },
                    },
                  }}
                >
                  <Markdown>{reviewerName}</Markdown>
                </Text>
                {listDes.length > 0 && (
                  <Box
                    sx={{
                      color: '#fff',
                      fontSize: '12px',
                      lineHeight: '140%',
                      'span + span': {
                        ' &:before ': {
                          content: '" · "',
                        },
                      },
                    }}
                  >
                    <div
                      dangerouslySetInnerHTML={{
                        __html: listDes.join(''),
                      }}
                    />
                  </Box>
                )}
              </Box>
            </Flex>
          )}
        </Box>
        <Box className={classes.actions}>
          <Button
            component={'a'}
            {...anchorProps}
            variant="white"
            rightIcon={<ArrowRight size={16} color={color} />}
            styles={(theme) => ({
              root: {
                background: theme.fn.lighten(color, 0.9),
                '&:hover': {
                  background: '#fff',
                },
              },
            })}
            data-event-category="Service Connection"
            data-event-action="Risk Screening Banner Click"
            data-event-label={url}
            onClick={() => window.open(url)}
          >
            <Text size="s4" weight="semiBold" color={color}>
              {f({
                id: 'multiRiskEmbed.cta',
                defaultMessage: 'Assess your risk here',
              })}
            </Text>
          </Button>
        </Box>
      </Box>
    </Box>
  )

  useEffect(() => {
    setUrl(
      (v) => `${v}?source_link=${encodeURIComponent(window.location.href)}`,
    )
  }, [])
  return isMobile ? (
    <a href={url} target="_blank">
      {content}
    </a>
  ) : (
    content
  )
}

export { RiskScreenerEmbedItem }
